import React from 'react'

import { withStyles } from '@material-ui/core/styles'
import ReactGeoLayerTree from '@terrestris/react-geo/dist/LayerTree/LayerTree'
import isEqual from 'lodash/isEqual'
import { getUid } from 'ol'
import OLLayerGroup from 'ol/layer/Group'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import * as PoziUtils from 'api/utils'
import { getMap } from 'utils/MapUtils'

import LayerTreeNodeTitle from './LayerTreeNodeTitle'

const styles = theme => ({
  // Override default LayerTree styling (which is based on antd)
  root: {
    backgroundColor: 'transparent',
    fontSize: 15,
    marginBottom: 5,
    marginLeft: 8,
    marginTop: 5,
    '& .ant-tree .ant-tree-treenode': {
      paddingBottom: 6 // originally 4px
    },
    '& .ant-tree-treenode': {
      paddingTop: 4,
      // outline: 'solid 1px red',
      width: '100%',
      minHeight: 32 // // align with table in LayerHeader
    },
    '& .ant-tree-switcher': {
      display: props => props.printUIVisible ? 'none' : 'block', // Hide when in print mode
      alignSelf: 'unset' // To fix vertical alignment
    },
    // Make whole row highlight on hover
    '& .ant-tree .ant-tree-node-content-wrapper:hover': {
      backgroundColor: 'unset'
    },
    '& .ant-tree-treenode:hover': {
      backgroundColor: '#f5f5f5'
    },

    // Highlight closed parent node if it has a selected descendant
    '& .ant-tree-treenode-checkbox-checked > .ant-tree-node-content-wrapper-close': {
      fontWeight: 'bold'
    },
    '& .ant-tree-treenode-checkbox-indeterminate > .ant-tree-node-content-wrapper-close': {
      fontWeight: 'bold'
    },

    '& .ant-tree-node-content-wrapper': {
      flex: 1
    },
    '& .ant-tree-node-selected': {
      color: 'inherit',
      backgroundColor: 'inherit !important'
    },
    '& .ant-tree-checkbox-checked .ant-tree-checkbox-inner ': {
      backgroundColor: 'rgba(0, 0, 0, 0.54)',
      borderColor: 'rgba(0, 0, 0, 0.54)'
    },
    '& .ant-tree-checkbox-inner:hover': {
      borderColor: 'inherit'
    },
    '& .ant-tree-checkbox-checked:after': {
      borderColor: 'inherit'
    },
    '& .ant-tree-checkbox-indeterminate .ant-tree-checkbox-inner:after': {
      backgroundColor: 'rgba(0, 0, 0, 0.54)',
      // Make indeterminate block sligthly smaller
      height: 7,
      width: 7
    },
    '& .ant-tree-switcher-icon': {
      fontSize: 20,
      color: 'rgba(0, 0, 0, 0.54)'
    },
    '& .ant-tree-switcher_close': {
      marginRight: -8 // Make labels align
    },
    // Don't show hand pointer for labels that cannot be clicked on
    // '& .ant-tree-node-content-wrapper-close, .ant-tree-node-content-wrapper-open': {
    //   cursor: 'initial'
    // },
    '& .ant-tree-checkbox': {
      // margin: '4px 0 0 0'
      display: props => props.printUIVisible ? 'none' : 'block', // Hide when in print mode
      marginInline: 4
    },
    '& .ant-tree-checkbox-inner': {
      border: '2px solid rgba(0, 0, 0, 0.54)'
      // marginRight: 4
    },
    '& .ant-tree-checkbox-checked .ant-tree-checkbox-inner:after': {
      left: 2 // Make checkbox centred again due to 2px border
    },
    // Don't indent if there is no collapse/expand icon
    '& .ant-tree-switcher.ant-tree-switcher-noop': {
      display: 'none'
    },
    // Double indents to keep layers visually nested
    '& .ant-tree-indent-unit': {
      width: props => props.printUIVisible ? 30 : 48 // Reduce spacing when in print mode
    },
    // Hide checkbox for collapsed nodes but exclude leaf nodes
    '& .ant-tree-treenode-switcher-close > .ant-tree-checkbox > .ant-tree-checkbox-inner': {
      // '& .ant-tree-treenode-switcher-close:not(.ant-tree-treenode-leaf-last) > .ant-tree-checkbox': {
      // display: 'none'
      // visibility: 'hidden'
      // transition: 'all 1s 1s',
      border: 'none',
      // marginRight: 'inherit',
      width: 0
    },
    // Indeterminate checkbox needs different treatment to be hidden
    '& .ant-tree-treenode-switcher-close > .ant-tree-checkbox-indeterminate .ant-tree-checkbox-inner:after': {
      width: 0
    },
    '& .ant-tree-switcher-noop ~ .ant-tree-checkbox > .ant-tree-checkbox-inner': {
      // '& .ant-tree-treenode-switcher-close:not(.ant-tree-treenode-leaf-last) > .ant-tree-checkbox': {
      // display: 'block'
      // visibility: 'inherit'
      border: '2px solid rgba(0, 0, 0, 0.54)', // hardcoded as we cannot easily inherit
      // marginRight: 4,
      width: '16px' // hardcoded as we cannot easily inherit
      // border: 'inherit',
      // outline: 'solid 2px red'
    }
  },

  legend: {
    // Adjust legend image (WMS) to align with layer title
    '& a img': {
      marginLeft: -4
    },
    // Adjust GeoStyler legend (GeoJSON/WFS) to align with layer title
    '& svg': {
      marginLeft: -2
    }
    // marginLeft: -2 // Align with layer title
  }
})

class LayerTree extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      contextMenu: null,
      checkedKeys: [],
      expandedKeys: [],
      halfCheckedKeys: [],
      layers: {}
    }

    // Store reference to OL map
    this.map = getMap()
  }

  // Derive keys for nodes that will need to be expanded to show the
  // selected layers on mount
  componentDidMount () {
    // console.debug('LayerTree - componentDidMount')

    this.setLayersState()
    this.synchroniseTreeExpanded()
    this.synchroniseTreeChecked()
  }

  componentDidUpdate (prevProps, prevState) {
    // console.debug('LayerTree - componentDidUpdate props', prevProps, this.props)
    // console.debug('LayerTree - componentDidUpdate state', prevState, this.state)

    const prevTabLayersFilter = prevProps.tabLayersFilter
    const nextTabLayersFilter = this.props.tabLayersFilter

    if (nextTabLayersFilter !== prevTabLayersFilter) {
      // console.debug('componentDidUpdate - filter update', nextTabLayersFilter, prevTabLayersFilter)
      this.synchroniseTreeExpanded()
    }

    // In print mode, make sure all visible layers with a legend are expanded
    const printUIVisibleChanged = prevProps.printUIVisible !== this.props.printUIVisible
    if (printUIVisibleChanged) {
      this.synchroniseTreeExpanded()
    }

    const prevCheckedKeys = prevState.checkedKeys
    const nextCheckedKeys = this.state.checkedKeys
    const checkKeysChanged = !isEqual(prevCheckedKeys, nextCheckedKeys)

    // Change in checked keys. loop through all changed layers and set visibility based
    // on their new checked status
    if (checkKeysChanged) {
      const newCheckedKeys = nextCheckedKeys.filter(k => !prevCheckedKeys.includes(k))
      const newUncheckedKeys = prevCheckedKeys.filter(k => !nextCheckedKeys.includes(k))

      // console.debug('LayerTree - newCheckedKeys', newCheckedKeys, prevCheckedKeys, nextCheckedKeys)
      // console.debug('LayerTree - newUnheckedKeys', newUncheckedKeys, prevCheckedKeys, nextCheckedKeys)

      // Synchronise layer visibility with (un)checked keys
      // TODO: check if we can do this in a more elegant way. Maybe it's just as quick
      //       to loop through all layers to see if they match the checked keys?
      //       Or maybe create an array of changed keys with corresponding visibility?
      const layers = this.state.layers
      for (const key of newCheckedKeys) {
        const olLayer = layers[key]

        if (!olLayer) {
          console.log('componentDidUpdate - new checked keys - layer is undefined. Ignoring', olLayer)
          return
        }

        const isGroup = olLayer instanceof OLLayerGroup

        // const titleid = olLayer.get('titleid')
        // console.debug('LayerTree - new checked layer', key, titleid, isGroup, olLayer)

        if (isGroup) {
          // console.debug('LayerTree - new checked layer is group. Ignoring.', key, titleid)
          continue
        }

        const layerVisible = olLayer.getVisible()
        if (!layerVisible) {
          // console.debug('LayerTree - new checked layer set visible', key, titleid)
          olLayer.setVisible(true)
        }
      }

      for (const key of newUncheckedKeys) {
        const olLayer = layers[key]

        if (!olLayer) {
          console.log('componentDidUpdate - new unchecked keys - layer is undefined. Ignoring', olLayer)
          return
        }

        const isGroup = olLayer instanceof OLLayerGroup

        // const titleid = olLayer.get('titleid')
        // console.debug('LayerTree - new unchecked layer', key, titleid, isGroup, olLayer)

        if (isGroup) {
          // console.debug('LayerTree - new unchecked layer is group. Ignoring.', key, titleid)
          continue
        }

        // Hide all layers that have become unchecked (we make sure that we only operate
        // on layers that are currently matching the search filter)
        const layerVisible = olLayer.getVisible()
        const layerMatchesFilter = this.filterFunction(olLayer)
        if (layerVisible && layerMatchesFilter) {
          // console.debug('LayerTree - new checked layer set not visible', key, titleid)
          olLayer.setVisible(false)
        }
      }

      // Refresh layertree
      this.synchroniseTreeChecked()
    }
  }

  setLayersState = () => {
    const { olLayer } = this.props

    const childLayers = PoziUtils.getAllOLLayersAndGroups(olLayer)

    const layers = {}
    for (const childLayer of childLayers) {
      layers[childLayer.ol_uid] = childLayer
    }

    this.setState({ layers })
  }

  synchroniseTreeExpanded = () => {
    const expandedKeys = this.getExpandedKeys()

    this.setState({
      expandedKeys
    })
  }

  synchroniseTreeChecked = () => {
    const checkedState = this.getCheckedStateFromLayers()
    // console.debug('LayerTree - synchroniseTreeChecked', checkedState)

    this.setState({
      ...checkedState
    })
  }

  // This function gets applied to any node (parent and leaf nodes) in the LayerTree
  // and if a leaf node matches, we'll need to make sure that the parent returns true too.
  // This has been taken care of by BuildKeywords
  filterFunction = (olLayer) => {
    const { printUIVisible, tabLayersFilter, topLevelLayerConf } = this.props
    const { checkedKeys, halfCheckedKeys } = this.state

    const titleid = olLayer.get('titleid')
    const layerConf = PoziUtils.getLayerFromTitleID(titleid)
    const showInLayerControl = layerConf.showInLayerControl ?? true
    const layerGroup = layerConf.group
    const topLevelGroup = topLevelLayerConf.group

    // console.debug('filterFunction - layerConf', titleid, showInLayerControl, printUIVisible)

    if (printUIVisible) {
      const visibleLayersWithLegend = PoziUtils.getVisibleLayersWithLegend()
      // console.debug('filterFunction - visibleLayersWithLegend', titleid, visibleLayersWithLegend)

      const showLayer = visibleLayersWithLegend.some(layerConf => layerConf.titleid === titleid)

      if (showLayer) {
        // console.debug('filterFunction - printUIVisible showLayer', titleid, showLayer)
      }

      return showLayer
    }

    if (!showInLayerControl) {
      // console.debug('filterFunction - showInLayerControl set to false, returning false', titleid)
      return false
    }

    if (layerGroup && layerGroup !== topLevelGroup) {
      // console.debug('filterFunction - group keyword is different from overarching group', titleid, layerGroup, topLevelGroup)
      return false
    }

    if (!tabLayersFilter) {
      // console.debug('filterFunction - no filter, returning true')
      return true
    }

    const keywords = layerConf?.keywords
    if (!keywords) {
      // console.debug('filterFunction - no filter found in layerConf. Returning true.', titleid)
      return true
    }

    const match = keywords.includes(tabLayersFilter.toLowerCase())

    // console.debug('filterFunction - match', `*${titleid}*`, getUid(olLayer), tabLayersFilter, !!match, filter)

    return match
  }

  getAllChildNodes = (node, childNodes = []) => {
    // console.debug('LayerTree - getAllChildNodes', node, childNodes)

    const children = node.children || []
    for (const childNode of children) {
      childNodes.push(childNode)

      // recurse
      if (childNode.children) {
        this.getAllChildNodes(childNode, childNodes)
      }
    }

    return childNodes
  }

  // This method returns the ids for the leaf nodes that are visible children
  getCheckedKeys = () => {
    const { olLayer } = this.props

    const childLayers = PoziUtils.getAllOLLayersAndGroups(olLayer)

    const checkedKeys = childLayers
      .filter(l => !(l instanceof OLLayerGroup)) // we only want CHILD layers
      .filter(l => l.getVisible()) // we only want visible layers
      .map(getUid) // the uid is the key that's used in the LayerTree

    // console.debug('LayerTree - getCheckedKeys', olLayer.get('titleid'), checkedKeys)

    return checkedKeys
  }

  // This method returns the ids for the nodes that either have visible children or match the
  // layer search filter
  getExpandedKeys = () => {
    const { olLayer, printUIVisible, tabLayersFilter } = this.props

    const childLayers = PoziUtils.getAllOLLayersAndGroups(olLayer)

    // The default function for expanding layertree nodes to show all visible leaf nodes
    const filterFnVisible = (olLayer) => olLayer.getLayersArray().some(l => l.getVisible())

    // This function will be applied when there is a change in the search terms for the
    // layer search and will expand all parent nodes of nodes that match those search terms
    const filterFnMatch = (olLayer) => {
      const layerConf = PoziUtils.getLayerFromOLLayer(olLayer)
      const keywords = layerConf?.keywords || ''
      return keywords.includes(tabLayersFilter.toLowerCase())
    }

    // Only use filter function if a layer filter has been provided and we're not generating
    // a PDF for printing.
    const useFilterFn = tabLayersFilter && !printUIVisible

    const filterFn = useFilterFn ? filterFnMatch : filterFnVisible

    const expandedKeys = childLayers
      .filter(l => l instanceof OLLayerGroup) // we only want parent layers
      .filter(filterFn) // that have at least one visible child
      .map(getUid) // the uid is the key that's used in the LayerTree

    // console.debug('LayerTree - getExpandedKeys', olLayer.get('titleid'), expandedKeys)

    return expandedKeys
  }

  // This method returns the ids for the leaf nodes that are visible children
  getCheckedStateFromLayers = () => {
    const { olLayer } = this.props

    if (!olLayer) {
      console.log('getCheckedStateFromLayers - no layer in props. Doing nothing', olLayer)
      return
    }

    const state = {
      checkedKeys: [],
      halfCheckedKeys: []
    }

    // console.debug('getCheckedStateFromLayers - TOP LAYER', olLayer.get('titleid'), olLayer.ol_uid, olLayer)

    const getChecked = (layer, state) => {
      const key = layer.ol_uid
      // const titleid = layer.get('titleid')

      // Handle case where provided argument is a non-group OL layer (i.e. leaf node)
      if (!(layer instanceof OLLayerGroup)) {
        // console.debug('getCheckedStateFromLayers - leaf node', titleid, key, layer)
        const checked = layer.getVisible()
        if (checked) {
          // console.debug('getCheckedStateFromLayers - leaf node - checked', titleid, key)
          state.checkedKeys.push(key)
        }

        return
      }

      // console.debug('getCheckedStateFromLayers - parent node', titleid, key, layer)

      // Recursively loop through child layers to get them to be marked as (half-)checked
      const childLayers = layer.getLayers().getArray()
      for (const childLayer of childLayers) {
        getChecked(childLayer, state)
      }

      const childKeys = childLayers.map(childLayer => childLayer.ol_uid)

      const allChecked = childKeys.every(childKey => state.checkedKeys.includes(childKey))
      if (allChecked) {
        // console.debug('getCheckedStateFromLayers - parent node - all checked', titleid, key)
        state.checkedKeys.push(key)
        return
      }

      const halfChecked = childKeys.some(childKey => state.checkedKeys.includes(childKey) || state.halfCheckedKeys.includes(childKey))
      if (halfChecked) {
        // console.debug('getCheckedStateFromLayers - parent node - half checked', titleid, key)

        state.halfCheckedKeys.push(key)
      }
    }

    getChecked(olLayer, state)

    // console.debug('getCheckedStateFromLayers - FINAL STATE', state)

    return state
  }

  onCheck = (checkedKeysValue, evt) => {
    const { node } = evt

    const key = node.key
    const { halfCheckedKeys } = this.state

    // console.debug('LayerTree - onCheck', key, checkedKeysValue, { ...evt })

    let newHalfCheckedKeys = evt.halfCheckedKeys

    let newCheckedKeys = [...checkedKeysValue]

    // A half-checked node has changed. We will need to uncheck it and all of its children
    if (checkedKeysValue.includes(key) && halfCheckedKeys.includes(key)) {
      // console.debug('LayerTree - click on halfChecked key', key, node)

      // Let list of all child keys and clicked key in order to be unchecked
      const childNodes = this.getAllChildNodes(node)
      const childNodesKeys = childNodes.map(childNode => childNode.key)

      const keyToBeUnchecked = [key, ...childNodesKeys]

      // console.debug('LayerTree - click on halfChecked key - childnodes', key, keyToBeUnchecked)

      // Remove the key from both the checked and half-checked keys arrays
      newCheckedKeys = checkedKeysValue.filter(value => !keyToBeUnchecked.includes(value))
      newHalfCheckedKeys = newHalfCheckedKeys.filter(value => !keyToBeUnchecked.includes(value))

      // console.debug('LayerTree - click on halfChecked key', key, newCheckedKeys)
    }

    // console.debug('LayerTree - new checkedKeys', newCheckedKeys)
    // console.debug('LayerTree - new halfCheckedKeys', newHalfCheckedKeys)

    this.setState({
      checkedKeys: newCheckedKeys,
      halfCheckedKeys: newHalfCheckedKeys
    })
  }

  getOlLayerFromKey = (key) => {
    const olLayer = PoziUtils.getAllOLLayersAndGroups().find(l => l.ol_uid === key)

    // console.debug('getOlLayerFromKey', key, olLayer)

    return olLayer
  }

  setOlLayerVisibility = (olLayer, visibility) => {
    const titleid = olLayer.get('titleid')

    // console.debug('LayerTree - toggleOlLayerVisibility', titleid, visibility, olLayer)

    olLayer.setVisible(visibility)
  }

  onExpand = (newExpandedKeys, { expanded, node }) => {
    const { expandedKeys } = this.state

    // console.debug('LayerTree - onExpand', expandedKeys, newExpandedKeys, expanded, node.key)

    this.setState({
      expandedKeys: node.expanded
        ? expandedKeys.filter(k => k !== node.key)
        : expandedKeys.concat(node.key)
    })
  }

  // When clicking on the layer title, this will toggle expand/collapse for parent nodes and for
  // leaf nodes, it will toggle checked/unchecked
  onSelect = (selectedKeys, evt) => {
    const { event, node } = evt

    // console.debug('LayerTree - onSelect', selectedKeys, node.checked, node?.title?.props?.titleid, node, { ...evt })

    const { checkedKeys, expandedKeys } = this.state

    const { checked, expanded, key } = node

    const nodeIsLeaf = !node.children
    if (nodeIsLeaf) {
      this.setState({
        checkedKeys: checked
          ? checkedKeys.filter(k => k !== key)
          : checkedKeys.concat(key)
      })
    } else {
      this.setState({
        expandedKeys: expanded
          ? expandedKeys.filter(k => k !== key)
          : expandedKeys.concat(key)
      })
    }
  }

  // This will show the layer title in the tree
  nodeTitleRenderer = (olLayer) => {
    // console.debug('LayerTree - nodeTitleRenderer', olLayer.get('titleid'), olLayer)

    // The zoomlevel is needed for redrawing in case there are scaale dependent layers
    const { mapZ } = this.props

    return (
      <LayerTreeNodeTitle
        key={`${olLayer.get('titleid')}-${mapZ}`}
        olLayer={olLayer}
      />
    )
  }

  render = () => {
    const { classes, olLayer, printUIVisible, tabLayersFilter, titleid } = this.props

    const { checkedKeys, expandedKeys } = this.state

    // console.debug('LayerTree - render', titleid, checkedKeys, tabLayersFilter, olLayer)

    if (!olLayer) {
      console.log('LayerTree - render - could not find OL layer. Not rendering.', titleid)
      return null
    }

    const key = `layertree-${tabLayersFilter}-${printUIVisible}` // needed for redrawing tree upon certain changes

    // See ANT Design tree docs: https://ant.design/components/tree/
    // For expand on click, see: https://codesandbox.io/s/9zv8y9ow4y
    return (
      <ReactGeoLayerTree
        className={classes.root}
        key={key}
        checkedKeys={checkedKeys}
        draggable={false}
        expandedKeys={expandedKeys}
        layerGroup={olLayer}
        nodeTitleRenderer={this.nodeTitleRenderer}
        map={this.map}
        onCheck={this.onCheck}
        onExpand={this.onExpand}
        onSelect={this.onSelect}
        filterFunction={this.filterFunction}
      />
    )
  }
}

LayerTree.propTypes = {
  classes: PropTypes.object.isRequired,
  layerConfs: PropTypes.object.isRequired,
  mapZ: PropTypes.number,
  olLayer: PropTypes.object,
  printUIVisible: PropTypes.bool,
  tabLayersFilter: PropTypes.string.isRequired,
  titleid: PropTypes.string.isRequired,
  topLevelLayerConf: PropTypes.object.isRequired
}

function mapStateToProps (state, ownProps) {
  const { layers: layerConfs, mapZ, printUIVisible, tabLayersFilter } = state
  const { titleid } = ownProps

  const topLevelLayerConf = layerConfs[titleid]

  let olLayer = PoziUtils.getOpenLayersObjectFromTitleID(titleid)

  // If 'folder' has been defined in a catalogue config, we will show the
  // catalogue title as a collapsible folder for the layers in that catalogue
  // This is useful when we show multiple catalogues within one group and want
  // to differentiate between those catalogues easily
  const folder = topLevelLayerConf.folder
  if (folder) {
    olLayer = new OLLayerGroup({ layers: [olLayer] })
  }

  const props = {
    layerConfs,
    mapZ,
    olLayer,
    printUIVisible,
    tabLayersFilter,
    topLevelLayerConf
  }

  return props
}

export default connect(mapStateToProps)(withStyles(styles)(LayerTree))
